// Wave 20.2 — Emoji burst overlay for Drop Link landing.
//
// CSS-only. Renders a pointer-events:none full-viewport canvas of
// falling 3D-style emojis + a flame plume behind the header. Fires
// once on mount, then removes itself after the animation ends.
//
// Deliberately no library: keeps the shared page's cold-start under
// the JS budget that matters for viral link opens.

import React, { useEffect, useMemo, useState } from 'react';
import { Box, keyframes } from '@mui/material';

const EMOJIS = ['🔥', '⚡', '🚀', '🏆', '💨', '💥', '⭐', '🎉'];

const fall = keyframes`
  0%   { transform: translate3d(var(--x0), -60px, 0) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translate3d(var(--x1), calc(100vh + 40px), 0) rotate(var(--rot)); opacity: 0; }
`;

const burst = keyframes`
  0%   { transform: translate3d(0, 30vh, 0) scale(0.4); opacity: 0; }
  20%  { opacity: 1; }
  60%  { transform: translate3d(var(--bx), -10vh, 0) scale(1.1) rotate(var(--rot)); opacity: 1; }
  100% { transform: translate3d(var(--bx), 90vh, 0) scale(0.9) rotate(calc(var(--rot) * 2)); opacity: 0; }
`;

interface Particle {
  id: number;
  emoji: string;
  left: string;
  x0: string;
  x1: string;
  rot: string;
  bx: string;
  style: 'fall' | 'burst';
  delay: string;
  duration: string;
  size: number;
}

export default function SharedCelebration() {
  const [visible, setVisible] = useState(true);
  const particles = useMemo<Particle[]>(() => seed(48), []);

  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 3800);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {particles.map((p) => (
        <Box
          key={p.id}
          component="span"
          sx={{
            position: 'absolute',
            top: 0,
            left: p.left,
            fontSize: `${p.size}px`,
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))',
            animation: `${p.style === 'fall' ? fall : burst} ${p.duration} ${p.delay} both cubic-bezier(0.22, 0.61, 0.36, 1)`,
            '--x0': p.x0,
            '--x1': p.x1,
            '--rot': p.rot,
            '--bx': p.bx,
          } as any}
        >
          {p.emoji}
        </Box>
      ))}
    </Box>
  );
}

function seed(count: number): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const style = i % 2 === 0 ? 'burst' : 'fall';
    const drift = (Math.random() - 0.5) * 60;
    out.push({
      id: i,
      emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
      left: style === 'burst' ? '50%' : `${Math.random() * 100}%`,
      x0: `${drift}px`,
      x1: `${drift * 3}px`,
      rot: `${(Math.random() - 0.5) * 720}deg`,
      bx: `${(Math.random() - 0.5) * 80}vw`,
      style,
      delay: `${Math.random() * 400}ms`,
      duration: `${2200 + Math.random() * 1000}ms`,
      size: 24 + Math.floor(Math.random() * 22),
    });
  }
  return out;
}
